import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../contexts/GameContext';
import { useAudio } from '../contexts/AudioContext';
import '../styles/GameScreen.css';

const GameScreen = () => {
  const navigate = useNavigate();
  const { gameState, honeyTypes, selectHoney, useHint: requestHint, finishGame, resetGame } = useGame();
  const { playSound, playCustomMusic, stopMusic, playMusic } = useAudio();
  const [activeHintIndex, setActiveHintIndex] = useState(null);
  const [timer, setTimer] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);

  // Verificar si el juego ha comenzado
  useEffect(() => {
    if (!gameState.started) {
      navigate('/');
    }
  }, [gameState.started, navigate]);

  // Reproducir la música de degustación al cargar la pantalla de juego
  useEffect(() => {
    // Detener cualquier música que esté sonando
    // Ya no detenemos la música ni reproducimos la música de degustación
    // para mantener la música de la página principal
    console.log('Manteniendo la música de la página principal durante el juego');
    
    // Limpiar al desmontar el componente
    return () => {
      // No detenemos la música al desmontar para mantener la continuidad
      console.log('Manteniendo la música al salir de la pantalla de juego');
    };
  }, [playCustomMusic, stopMusic]);

  // Manejador para seleccionar un tipo de miel
  const handleHoneySelection = (position, honeyId) => {
    selectHoney(position, honeyId);
  };

  // Manejador para solicitar una pista
  const handleHintRequest = (index) => {
    if (gameState.hintsUsed < 3) {
      requestHint();
      setActiveHintIndex(index);
    }
  };

  // Manejador para cerrar el modal de pista
  const handleCloseHint = () => {
    setActiveHintIndex(null);
  };

  // Manejador para confirmar selecciones
  const handleConfirmSelections = () => {
    // Verificar si se han seleccionado todas las mieles
    const allSelected = gameState.selections.length === 3 && 
                        gameState.selections.every(selection => selection);
    
    if (allSelected) {
      setShowConfirmation(true);
    } else {
      alert('Por favor, selecciona un tipo para cada miel antes de continuar.');
    }
  };

  // Manejador para finalizar el juego
  const handleFinishGame = () => {
    finishGame();
    navigate('/results');
  };

  // Manejador para volver a la página principal
  const handleBackToHome = () => {
    // Detener la música inmediatamente cuando se hace clic en "Terminar Juego"
    console.log('Deteniendo música al iniciar proceso de terminar juego...');
    stopMusic();
    
    // Mostrar modal de confirmación
    setShowExitConfirmation(true);
  };

  // Manejador para confirmar salir del juego
  const confirmExit = () => {
    // Asegurarnos de que la música esté completamente detenida
    if (typeof stopMusic === 'function') {
      console.log('Deteniendo música al confirmar salir del juego...');
      stopMusic();
    }
    
    // Reiniciar el estado del juego
    resetGame();
    
    // Establecer una bandera en localStorage para indicar que venimos de "Terminar Juego"
    // Esta bandera será verificada en Home.js para no reproducir música automáticamente
    localStorage.setItem('mielMaster_musicStopped', 'true');
    
    // Navegar a la página principal después de un breve retraso
    // para asegurar que la música se detenga completamente
    setTimeout(() => {
      navigate('/');
    }, 100);
  };

  return (
    <div className="game-screen-container">
      <div className="game-header">
        <h1>Degustá y adiviná</h1>
        {timer && (
          <div className="game-timer">
            <span className="timer-label">Tiempo:</span>
            <span className="timer-value">{formatTime(timeLeft)}</span>
          </div>
        )}
        <div className="header-score">
          <span className="score-value" style={{ color: '#f5a623', fontWeight: 700, fontSize: '2.5rem', textShadow: '1px 1px 2px rgba(0, 0, 0, 0.2)' }}>{gameState.score}</span>
        </div>
      </div>

      <div className="honey-samples-container">
        {[0, 1, 2].map((index) => (
          <div key={index} className="honey-sample">
            <div className="sample-header">
              <h2>Muestra {index + 1}</h2>
              <button 
                className="hint-button"
                onClick={() => handleHintRequest(index)}
                disabled={gameState.hintsUsed >= 3}
              >
                <span className="hint-icon">?</span>
                <span className="hint-text">Pista</span>
              </button>
            </div>
            
            <div className="honey-jar">
              <div 
                className="honey-liquid"
                style={{ backgroundColor: '#f5a623' }}
              ></div>
              <div className="honey-jar-lid"></div>
            </div>
            
            <div className="honey-selection">
              <div className="selection-options">
                {honeyTypes.map((honey) => (
                  <button
                    key={honey.id}
                    className={`selection-option ${gameState.selections[index] === honey.id ? 'selected' : ''}`}
                    onClick={() => handleHoneySelection(index, honey.id)}
                  >
                    {honey.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="game-controls">
        <div className="hints-counter">
          Pistas disponibles: {Math.max(0, 3 - gameState.hintsUsed)} de 3
        </div>
        <div className="game-buttons">
          <button 
            className="confirm-btn"
            onClick={handleConfirmSelections}
          >
            Confirmar Selecciones
          </button>
          <button 
            className="exit-btn"
            onClick={handleBackToHome}
          >
            Terminar Juego
          </button>
        </div>
      </div>

      {/* Modal de pista */}
      {activeHintIndex !== null && (
        <div className="hint-modal-overlay">
          <div className="hint-modal">
            <button className="close-hint" onClick={handleCloseHint}>×</button>
            <h3>Pista sobre la Muestra {activeHintIndex + 1}</h3>
            <div className="hint-content">
              <div className="bee-animation"></div>
              <div className="hint-characteristics">
                <h4>Características:</h4>
                <ul>
                  {gameState.correctAnswers && gameState.correctAnswers[activeHintIndex] && 
                    honeyTypes.find(h => h.id === gameState.correctAnswers[activeHintIndex])
                      .characteristics.slice(0, 2).map((char, idx) => (
                        <li key={idx}>{char}</li>
                      ))
                  }
                </ul>
              </div>
            </div>
            <div className="hint-footer">
              <span className="points-deducted">-50 puntos</span>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de selecciones */}
      {showConfirmation && (
        <div className="confirmation-modal-overlay">
          <div className="confirmation-modal">
            <h3>¿Estás seguro?</h3>
            <p>Una vez confirmadas tus selecciones, no podrás cambiarlas.</p>
            <div className="confirmation-buttons">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowConfirmation(false)}
              >
                Volver
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleFinishGame}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación para salir del juego */}
      {showExitConfirmation && (
        <div className="confirmation-modal-overlay">
          <div className="confirmation-modal">
            <h3>¿Quieres terminar el juego?</h3>
            <p>Si sales ahora, perderás tu progreso actual.</p>
            <div className="confirmation-buttons">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowExitConfirmation(false)}
              >
                Seguir Jugando
              </button>
              <button 
                className="btn btn-primary"
                onClick={confirmExit}
              >
                Terminar Juego
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Función auxiliar para obtener el color representativo de cada miel
function getHoneyColor(honeyId) {
  switch (honeyId) {
    case 'citricos':
      return '#f8e8a0'; // Blanco agua a ámbar extra claro (color claro)
    case 'eucalipto':
      return '#c9a66b'; // Ámbar claro a ámbar (66-89 mm Pfund)
    case 'treboles':
      return '#f2e6b1'; // Blanco a ámbar extra claro (16-50 mm Pfund)
    default:
      return '#f5a623';
  }
}

// Función auxiliar para formatear el tiempo
function formatTime(seconds) {
  if (!seconds) return '00:00';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

export default GameScreen;
